// src/views/proprio/_proprioUi.jsx — primitives UI partagées espace proprio.
//
// États standardisés (chargement / erreur / vide) + carte + en-tête de section,
// à la charte DS admin. Aucune logique métier ici (présentation pure).

import { useState, useRef, useCallback } from 'react'
import { AlertTriangle, Eye } from 'lucide-react'
import { DS } from '../../lib/ui'

// Carte DA : blanc franc, hairline 0.5px, coins radiusCard, ombre douce discrète.
export const card = {
  background: DS.card,
  borderRadius: DS.radiusCard,
  border: `0.5px solid ${DS.border}`,
  boxShadow: '0 1px 3px rgba(33,83,135,.05), 0 4px 14px rgba(33,83,135,.05)',
  padding: 18,
  marginBottom: 14,
}

// En-tête de vue — CARTE DE TITRE (lot apparence 2026-08-14, même gabarit que
// les autres espaces) : fond ciel pâle, trait navy qui relie au bandeau,
// titre Fraunces 22, sous-titre 12.5. La pastille d'icône ciel reste : c'est un
// repère de lecture, pas un élément technique. Un seul composant pour les 8
// vues proprio — l'uniformité tient toute seule.
export function SectionTitle({ children, sub, icon: IconCmp }) {
  return (
    <header style={{ background: '#e8f2fa', borderRadius: 12, padding: '16px 18px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
      <span aria-hidden="true" style={{ width: 3, height: 40, borderRadius: 2, background: '#3d5166', flexShrink: 0 }} />
      {IconCmp && (
        <span style={{ flex: '0 0 auto', width: 34, height: 34, borderRadius: 9, background: '#fff', color: DS.marine, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconCmp size={18} strokeWidth={1.8} />
        </span>
      )}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: DS.fontDisplay, fontSize: 22, fontWeight: 600, color: '#2e3d4d', lineHeight: 1.15 }}>{children}</div>
        {sub && <div style={{ fontSize: 12.5, color: '#5c7d99', marginTop: 3 }}>{sub}</div>}
      </div>
    </header>
  )
}

export function Loading({ label = 'Chargement…' }) {
  return (
    <div style={{ ...card, textAlign: 'center', color: DS.textMuted, fontSize: 13 }}>
      {label}
    </div>
  )
}

export function ErrorBox({ message, onRetry }) {
  return (
    <div style={{ ...card, background: DS.dangerBg, border: `0.5px solid ${DS.danger}33`, borderLeft: `3px solid ${DS.danger}` }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, color: DS.dangerText, fontWeight: 600, fontSize: 13, marginBottom: onRetry ? 10 : 0 }}>
        <AlertTriangle size={16} strokeWidth={1.8} style={{ flex: '0 0 auto', marginTop: 1 }} />
        <span>{message || 'Une erreur est survenue.'}</span>
      </div>
      {onRetry && (
        <button onClick={onRetry} className="fbz-press"
          style={{ background: DS.corail, color: '#fff', border: 'none', borderRadius: DS.radiusControl, padding: '8px 15px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: DS.fontBody }}>
          Réessayer
        </button>
      )}
    </div>
  )
}

export function EmptyBox({ children }) {
  return (
    <div style={{ ...card, textAlign: 'center', color: DS.textMuted, fontSize: 13 }}>
      {children}
    </div>
  )
}

// ── InfoDot (tooltip ⓘ maison — aucune dépendance externe) ──
// Petite icône « ⓘ » discrète (DS muted) révélant une courte explication :
//   - survol desktop (mouseenter/leave)
//   - tap mobile (click toggle)
//   - focus clavier (tabIndex=0, ouverture au focus, fermeture au blur + Échap)
// Accessibilité : role="button", aria-expanded, aria-label = texte d'explication.
// Présentation pure — aucun calcul métier. Prop : InfoDot({ text }).
export function InfoDot({ text }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setOpen(false)
      // rend la main : on retire le focus pour fermer proprement.
      if (wrapRef.current) wrapRef.current.blur()
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setOpen((v) => !v)
    }
  }, [])

  if (!text) return null

  return (
    <span
      ref={wrapRef}
      role="button"
      tabIndex={0}
      aria-label={text}
      aria-expanded={open}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        setOpen((v) => !v)
      }}
      onKeyDown={onKeyDown}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 14,
        height: 14,
        marginLeft: 4,
        borderRadius: '50%',
        border: `1px solid ${DS.textLight}`,
        color: DS.textLight,
        fontSize: 10,
        fontWeight: 700,
        lineHeight: 1,
        cursor: 'help',
        userSelect: 'none',
        verticalAlign: 'middle',
        fontFamily: DS.fontBody,
      }}
    >
      <span aria-hidden="true">i</span>
      {open && (
        <span
          role="tooltip"
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 6px)',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'max-content',
            maxWidth: 220,
            background: DS.navy,
            color: '#fff',
            fontSize: 11.5,
            fontWeight: 500,
            lineHeight: 1.35,
            textAlign: 'left',
            padding: '7px 9px',
            borderRadius: 8,
            boxShadow: '0 4px 14px rgba(33,83,135,.28)',
            whiteSpace: 'normal',
            zIndex: 50,
            pointerEvents: 'none',
            fontFamily: DS.fontBody,
          }}
        >
          {text}
        </span>
      )}
    </span>
  )
}

// ── StatCard (promu depuis ProprioMenageView — réutilisé par les stats) ──
// Carte KPI compacte ; clickable si onClick fourni (chevron ▴/▾). tone : neutral
// (navy) · grey · red · green · sky (skyDeep) · terra.
// Prop optionnelle `info` (texte) → rend un InfoDot à droite du label (additif).
//
// VARIANTES (purement visuelles) :
//   - défaut (variant absent) → rendu COMPACT historique (utilisé par
//     ProprioMenageView). Strictement inchangé : ne pas régresser.
//   - variant="stat" → rendu PREMIUM dashboard Statistiques : chiffre KPI en
//     police display (Playfair), ombre douce, fond blanc, hover discret,
//     bande d'accent latérale teintée. `emphasis` (bool) → KPI primaire plus
//     grand/mis en avant ; `accent` (couleur) force la teinte d'accent.
export function StatCard({ label, value, tone, active, onClick, sub, info, variant, emphasis, accent }) {
  const tones = {
    neutral: { bg: DS.cielSoft, fg: DS.marine, br: DS.ciel },
    grey: { bg: '#f1f2f4', fg: DS.textMuted, br: DS.border },
    red: { bg: DS.dangerBg, fg: DS.danger, br: `${DS.danger}44` },
    green: { bg: DS.successBg, fg: DS.success, br: `${DS.success}44` },
    sky: { bg: DS.cielSoft, fg: DS.marine, br: DS.ciel },
    terra: { bg: DS.corailSoft, fg: DS.corailText, br: `${DS.corail}44` },
  }
  const t = tones[tone] || tones.neutral
  const clickable = typeof onClick === 'function'

  // ── Variante PREMIUM « stat » ────────────────────────────────────────────
  if (variant === 'stat') {
    const accentColor = accent || t.fg
    return (
      <StatCardStat
        label={label}
        value={value}
        info={info}
        sub={sub}
        emphasis={!!emphasis}
        accentColor={accentColor}
      />
    )
  }

  // ── Variante COMPACTE historique (ProprioMenageView) — inchangée ──────────
  const cardStyle = { flex: 1, minWidth: 96, textAlign: 'left', background: t.bg, border: `1px solid ${active ? t.fg : t.br}`, borderRadius: 12, padding: '10px 12px', cursor: clickable ? 'pointer' : 'default', fontFamily: DS.fontBody }
  const inner = (
    <>
      <div style={{ fontSize: 18, fontWeight: 800, color: t.fg, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 11.5, color: '#666', marginTop: 3 }}>{label}{info && <InfoDot text={info} />}{clickable && <span style={{ color: t.fg }}> {active ? '▴' : '▾'}</span>}</div>
      {sub != null && <div style={{ fontSize: 11, color: DS.textLight, marginTop: 2 }}>{sub}</div>}
    </>
  )
  // Cliquable → <button> (ProprioMenageView). Non cliquable (stats) → <div> :
  // un <button> disabled imbriquerait l'InfoDot dans un bouton désactivé qui
  // avale ses événements (tooltip jamais ouvert — bug attrapé en Playwright).
  if (clickable) {
    return <button type="button" onClick={onClick} className="fbz-press" style={cardStyle}>{inner}</button>
  }
  return <div style={cardStyle}>{inner}</div>
}

// Carte KPI premium (dashboard Statistiques uniquement). Toujours <div> non
// cliquable → l'InfoDot reste accessible (cf. bug Playwright historique).
// Présentation pure : aucun calcul, aucune valeur/condition modifiée.
function StatCardStat({ label, value, info, sub, emphasis, accentColor }) {
  const [hover, setHover] = useState(false)
  const valueSize = emphasis ? 30 : 22
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        height: '100%',
        boxSizing: 'border-box',
        textAlign: 'left',
        background: DS.white,
        borderRadius: 16,
        padding: emphasis ? '18px 18px 16px 20px' : '14px 14px 13px 18px',
        // PAS d'overflow:hidden ici : il clipperait le popover de l'InfoDot
        // (positionné au-dessus de la carte). La bande d'accent ci-dessous
        // arrondit ses coins gauches pour épouser la carte sans débordement.
        boxShadow: hover
          ? '0 6px 22px rgba(33,83,135,.14), 0 2px 6px rgba(33,83,135,.08)'
          : '0 1px 3px rgba(33,83,135,.06), 0 6px 18px rgba(33,83,135,.05)',
        transform: hover ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'box-shadow .22s ease, transform .22s ease',
        fontFamily: DS.fontBody,
      }}
    >
      {/* Bande d'accent latérale (identité couleur FBZ par sémantique). */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: emphasis ? 4 : 3,
          // Coins gauches arrondis au rayon de la carte (16px) : sans
          // overflow:hidden, la bande ne doit pas dépasser les arrondis.
          borderRadius: '16px 0 0 16px',
          background: accentColor,
          opacity: emphasis ? 0.9 : 0.55,
        }}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '.04em',
          textTransform: 'uppercase',
          color: DS.textLight,
          marginBottom: emphasis ? 8 : 6,
        }}
      >
        <span>{label}</span>
        {info && <InfoDot text={info} />}
      </div>
      <div
        style={{
          fontFamily: DS.fontDisplay,
          fontSize: valueSize,
          fontWeight: 700,
          color: emphasis ? accentColor : DS.navy,
          lineHeight: 1.05,
          letterSpacing: '-.01em',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      {sub != null && (
        <div style={{ fontSize: 11, color: DS.textLight, marginTop: 5, lineHeight: 1.3 }}>{sub}</div>
      )}
    </div>
  )
}

// Palette répartition canaux = couleurs de SOURCE DA (DESIGN §8 bis, identiques au
// Calendrier) : Airbnb corail-rouge · Booking marine · Direct vert.
export const CHANNEL_COLORS = { airbnb: DS.srcAirbnb, booking: DS.srcBooking, direct: DS.srcDirect }

// ── Donut (SVG maison, max 3 parts, pct fournis) ──
// parts : [{ key, label, pct, value }] — pct en % (somme ≈ 100). Couleurs via
// CHANNEL_COLORS[key] (fallback navy). Rendu purement présentationnel (aucun calcul
// métier ici : les pct viennent du serveur). Tout-vide → cercle gris « — ».
export function Donut({ parts = [], size = 132, thickness = 16, title, centerLabel }) {
  const r = (size - thickness) / 2
  const cx = size / 2
  const cy = size / 2
  const circ = 2 * Math.PI * r
  const gap = 0.9 // petit espace blanc entre segments (en unités de longueur d'arc)
  const clean = (parts || []).filter((p) => p && Number(p.pct) > 0)
  const totalPct = clean.reduce((s, p) => s + Number(p.pct), 0)
  // Libellé central : UNIQUEMENT si fourni par l'appelant (aucune valeur
  // recalculée ici — présentation pure). Absent → anneau sans centre.
  const centerTotal = centerLabel != null ? centerLabel : null
  let offset = 0
  return (
    <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', width: size, height: size, flex: '0 0 auto' }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={title || 'Répartition'}>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={DS.border} strokeWidth={thickness} />
          {totalPct > 0 && clean.map((p) => {
            const frac = Number(p.pct) / 100
            const len = Math.max(0, circ * frac - (clean.length > 1 ? gap : 0))
            const seg = (
              <circle key={p.key} cx={cx} cy={cy} r={r} fill="none"
                stroke={CHANNEL_COLORS[p.key] || DS.navy} strokeWidth={thickness}
                strokeLinecap="round"
                strokeDasharray={`${len} ${circ - len}`}
                strokeDashoffset={-offset}
                transform={`rotate(-90 ${cx} ${cy})`} />
            )
            offset += circ * frac
            return seg
          })}
          {totalPct === 0 && (
            <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
              fontSize="15" fill={DS.textLight} fontFamily={DS.fontBody}>—</text>
          )}
        </svg>
        {totalPct > 0 && centerTotal != null && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <div style={{ fontFamily: DS.fontDisplay, fontSize: 19, fontWeight: 700, color: DS.navy, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              {centerTotal}
            </div>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: DS.textLight, marginTop: 3 }}>
              Total
            </div>
          </div>
        )}
      </div>
      <div style={{ fontSize: 12.5, color: DS.navy, fontFamily: DS.fontBody, minWidth: 120 }}>
        {clean.length === 0 && <div style={{ color: DS.textLight }}>Aucune donnée</div>}
        {clean.map((p) => (
          <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: CHANNEL_COLORS[p.key] || DS.navy, display: 'inline-block', flex: '0 0 auto', boxShadow: `0 0 0 3px ${(CHANNEL_COLORS[p.key] || DS.navy)}1a` }} />
            <span style={{ fontWeight: 600, minWidth: 52 }}>{p.label}</span>
            <span style={{ color: '#7a8aa0', fontVariantNumeric: 'tabular-nums' }}>{Number(p.pct).toFixed(0)} %{p.value != null ? ` · ${p.value}` : ''}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Primitives dashboard Statistiques (partagées proprio + admin) ──
// Extraites de ProprioStatsView pour partage propre avec AdminStatsView
// (vue « Statistiques » admin). Présentation pure — aucun calcul métier.

// Styles : grilles responsives + reveal en cascade (respecte
// prefers-reduced-motion). Injecter une fois via <style>{STATS_CSS}</style>.
export const STATS_CSS = `
.fbz-kpi-hero {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  align-items: stretch;
}
.fbz-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(168px, 1fr));
  gap: 12px;
  align-items: stretch;
}
.fbz-donuts {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}
.fbz-reveal {
  opacity: 0;
  transform: translateY(8px);
  animation: fbzRevealIn .5s cubic-bezier(.22,.61,.36,1) forwards;
}
@keyframes fbzRevealIn {
  to { opacity: 1; transform: translateY(0); }
}
@media (max-width: 860px) {
  .fbz-kpi-hero { grid-template-columns: repeat(2, 1fr); }
  .fbz-donuts { grid-template-columns: 1fr; }
}
@media (max-width: 560px) {
  .fbz-kpi-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (prefers-reduced-motion: reduce) {
  .fbz-reveal { animation: none; opacity: 1; transform: none; }
}
`

// Intitulé de section discret (majuscules espacées) — repère visuel sobre.
export function SubLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        color: DS.textLight,
        margin: '0 0 11px 2px',
        fontFamily: DS.fontBody,
      }}
    >
      {children}
    </div>
  )
}

// Wrapper de reveal en cascade (délai échelonné par index). Présentation pure.
// height:100% : occupe toute la cellule de grille (alignItems:stretch) pour
// aligner la hauteur des cartes voisines.
export function Reveal({ i = 0, children }) {
  return (
    <div
      className="fbz-reveal"
      style={{ height: '100%', animationDelay: `${Math.min(i, 14) * 45}ms` }}
    >
      {children}
    </div>
  )
}

// Carte conteneur d'un donut (titre + InfoDot + graphe). Style uniquement.
export function DonutCard({ title, info, parts }) {
  return (
    <div
      style={{
        background: DS.white,
        borderRadius: 16,
        boxShadow: '0 1px 3px rgba(33,83,135,.06), 0 6px 18px rgba(33,83,135,.05)',
        padding: 20,
      }}
    >
      <div
        style={{
          fontFamily: DS.fontDisplay,
          fontWeight: 700,
          color: DS.navy,
          fontSize: 15,
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {title}
        <InfoDot text={info} />
      </div>
      <Donut title={title} parts={parts} />
    </div>
  )
}

// Parts d'un donut depuis un bloc {airbnb,booking,direct} (count|pct OU ca|pct).
export function donutParts(block, valueKey) {
  if (!block) return []
  return ['airbnb', 'booking', 'direct'].map((k) => ({
    key: k,
    label: k === 'airbnb' ? 'Airbnb' : k === 'booking' ? 'Booking' : 'Direct',
    pct: block[k] ? block[k].pct : null,
    value: block[k] ? block[k][valueKey] : null,
  }))
}

// Mention lecture seule (rappel UX — le vrai verrou est backend).
export function ReadOnlyHint() {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: DS.textMuted, marginBottom: 12, fontStyle: 'italic' }}>
      <Eye size={13} strokeWidth={1.8} /> Consultation en lecture seule.
    </div>
  )
}
